import type { KAPLAYCtx } from '../types.ts'
import type { RoomOptions } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'

export function useTrainingRoom(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('trainingRoom', `sprites/rooms/training-room.png?${cacheStr}`)
  k.loadSprite('trainingRoomHover', `sprites/rooms/training-room--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON(
    'trainingRoomColliders',
    `colliders/training-room.parsed.json?${cacheStr}`,
  )

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function setTrainingRoom(roomOpts?: RoomOptions) {
    roomOpts = roomOpts || {}

    const roomObj = k.add()

    k.onLoad(() => {
      const { data } = collidersJson

      roomObj.add([
        k.sprite('trainingRoom', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('background'),
        'training_room_background',
        'background',
      ])

      roomObj.add([
        k.sprite('trainingRoomHover', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('hover'),
        'training_room_hover',
        'hover',
      ])

      data.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        roomObj.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      roomObj.add([
        k.level(
          [
            '              ',
            '              ',
            '              ',
            '           ?  ',
            '              ',
            '              ',
            '            1 ',
            '              ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {
              '1': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'training_room_door_exit',
                'door_exit',
                'door',
              ],
              '?': () => [
                k.sprite('overlay', { anim: 'question' }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'training_room_info',
                'sfx_emit_action',
                'info',
              ],
            },
          },
        ),
        k.layer('objects'),
        'training_room',
      ])

      // Set player position
      k.get('player')[0]?.setPos(k.vec2(getTiledSize(12.5), getTiledSize(5)))

      const doorExitCollision = k.onCollide('player', 'training_room_door_exit', () => {
        k.flash(k.BLACK, 0.5)
        roomObj.destroy()
        doorExitCollision.cancel()
        infoCollision.cancel()
        infoCollisionEnd.cancel()
        if (roomOpts.onExit) roomOpts.onExit()
      })

      const infoCollision = k.onCollide('player', 'training_room_info', () => {
        showTextFrame(getLangString('training_room_info'))
      })

      const infoCollisionEnd = k.onCollideEnd('player', 'training_room_info', () => {
        showTextFrame()
      })
    })

    return roomObj
  }

  return {
    setTrainingRoom,
  }
}
